import { useMemo, useState, useEffect, useRef } from "react";
import { Box, Typography, Chip, useTheme, Avatar, Button, Checkbox, Tooltip, IconButton, Slide } from "@mui/material";
import { blue } from "@mui/material/colors";
import { useEmployeeSelection } from "./hooks/useEmployeeSelection";
import { useBulkDownload } from "./hooks/useBulkDownload";
import BulkDownloadButton from "./BulkDownloadButton";
import { getEmployeeColumns } from "./employeeColumns";
import { useQuery, useLazyQuery } from "@apollo/client";
import {
  GET_STAFF_DETAILS,
} from "../../../../apis/organization-apis/people-apis/peopleApis";
import { GET_EVIDENCES_BY_EMPLOYEES_BATCH } from "../../../../apis/organization-apis/people-apis/peopleApis";
import { CheckStatus, EmptyFields } from "../../../../utils/enums";
import ReusableTable from "../../../../components/ReusableTable/ReusableTable";
import EvidenceTableContainer from "../common/EvidenceTableContainer";
import ResourceStatusCell from "../../../Checks/components/common/ResourceStatusCell";
import DownloadIcon from "@mui/icons-material/Download";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useParams, useLocation } from "react-router-dom";
import { useAuditContext } from "../../../../hooks/useAuditContext";
import { GET_USER_DETAILS } from "../../../../apis/dashboard-apis/dashboardDetailApi";
import Role from "../../../../utils/roles";
import { formatDate, formatDateForInput } from "../../../../utils/dateUtils";
import PolicyEvidenceTab from "../PolicyEvidence/PolicyEvidenceTab";
import LoadingToast from "../../../../components/LoadingToast";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";

const PeopleEvidenceTab = ({ checkDetails, activeCheckId, onRefetch, isAuditorView = false, showPolicyButton = false, defaultStatusFilter = [] }) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [joinDateFilter, setJoinDateFilter] = useState({ date: "", mode: null });
  const [statusFilter, setStatusFilter] = useState(defaultStatusFilter);
  const [evidenceFilter, setEvidenceFilter] = useState([]);
  const [roleFilter, setRoleFilter] = useState([]);
  const [employeeEvidenceCache, setEmployeeEvidenceCache] = useState({});
  const location = useLocation();
  const { auditId } = useParams();
  const fetchingRef = useRef(false);
  const lastFetchKeyRef = useRef(null);

  // Get audit from location state
  const audit = location.state?.audit;

  // Get user role
  const { data: userData } = useQuery(GET_USER_DETAILS);
  const userDetails = userData?.getAccountDetails?.userDetails || {};
  const isAuditor = userDetails?.roles?.some(
    (role) => role?.role_description === Role.AUDITOR
  );
  const userRole = isAuditor ? "auditor" : "admin";

  // Get audit context for date filtering
  const { auditStartDate, auditEndDate } = useAuditContext({
    auditId: audit?.id || auditId,
    userRole,
  });

  const resolvedAuditId = audit?.id || auditId;

  const { data: staffData } = useQuery(GET_STAFF_DETAILS, {
    variables: {
      filter: {},
      audit_id: resolvedAuditId,
    },
    skip: isAuditorView && !resolvedAuditId,
  });

  // Lazy query to fetch evidence for all employees in one go
  const [getEvidencesBatch] = useLazyQuery(GET_EVIDENCES_BY_EMPLOYEES_BATCH, {
    fetchPolicy: "network-only",
  });

  // Helper function to parse evidence from API response
  const parseEvidenceItem = (evidence) => {
    let metadata = {};
    try {
      if (evidence.checkmate_metadata) {
        metadata = typeof evidence.checkmate_metadata === 'string' 
          ? JSON.parse(evidence.checkmate_metadata) 
          : evidence.checkmate_metadata;
      }
    } catch (e) {
      console.error("Error parsing checkmate_metadata:", e);
    }

    const maskName = evidence.maskName || evidence.path?.split("/").pop() || "Unknown";
    
    // Extract file size from metadata or calculate from path
    const fileSize = metadata?.file_size || metadata?.size || "-";
    const fileSizeMB = fileSize ? (typeof fileSize === 'number' ? (fileSize / (1024 * 1024)).toFixed(1) + " MB" : fileSize) : "-";

    // Extract evidence type from metadata
    const evidenceType = metadata?.evidence_type || metadata?.type || "Document";

    return {
      id: evidence.id,
      maskName: maskName,
      evidenceName: maskName,
      evidenceType: evidenceType,
      collectedDate: evidence.created_at ? new Date(evidence.created_at) : null,
      uploadedBy: metadata?.uploaded_by || metadata?.created_by || "System",
      auditStatus: metadata?.audit_status || "Not reviewed",
      fileSize: fileSizeMB,
      path: evidence.path,
      checkId: evidence.check_id,
      entityType: evidence.entity_type,
      entityId: evidence.entity_id,
    };
  };

  // Create stable keys for dependency tracking
  const employeeResultsKey = useMemo(() => {
    if (!checkDetails?.employee_results) return null;
    return checkDetails.employee_results.map(r => r?.resource_name).sort().join(',');
  }, [checkDetails?.employee_results]);

  const staffDataKey = useMemo(() => {
    if (!staffData?.getEmployees?.data) return null;
    return staffData.getEmployees.data.map(e => e?.work_email).sort().join(',');
  }, [staffData?.getEmployees?.data]);

  const auditDatesKey = useMemo(() => {
    return auditEndDate || 'null';
  }, [auditEndDate]);

  // Fetch evidence for all employees
  useEffect(() => {
    if (!checkDetails?.employee_results || !staffData?.getEmployees?.data) {
      setEmployeeEvidenceCache({});
      return;
    }

    // Create a stable key to prevent unnecessary re-fetches
    const cacheKey = `${employeeResultsKey}-${staffDataKey}-${auditDatesKey}`;

    // Skip if already fetching or if we've already fetched this exact combination
    if (fetchingRef.current || lastFetchKeyRef.current === cacheKey) {
      return;
    }

    fetchingRef.current = true;
    lastFetchKeyRef.current = cacheKey;

    const fetchAllEvidence = async () => {
      // Collect all employee emails from results
      const employeeEmails = checkDetails.employee_results
        .map((res) => res?.resource_name)
        .filter((email) => !!email);

      if (employeeEmails.length === 0) {
        setEmployeeEvidenceCache({});
        fetchingRef.current = false;
        return;
      }

      try {
        const { data } = await getEvidencesBatch({
          variables: {
            employeeEmails,
            audit_id: resolvedAuditId,
            end_time: auditEndDate,
            check_id: activeCheckId ? parseInt(activeCheckId) : null,
          },
        });

        const raw = data?.getEvidencesByEmployeesBatch;
        if (!raw) {
          setEmployeeEvidenceCache({});
        } else {
          const byEmail = JSON.parse(raw);
          const parsedCache = {};

          employeeEmails.forEach((email) => {
            const evidences = byEmail[email] || [];
            parsedCache[email] = evidences.map(parseEvidenceItem);
          });

          setEmployeeEvidenceCache(parsedCache);
        }
      } catch (error) {
        console.error("Error fetching evidence batch:", error);
        setEmployeeEvidenceCache({});
      } finally {
        fetchingRef.current = false;
      }
    };

    fetchAllEvidence();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeResultsKey, staffDataKey, auditDatesKey]);

  // Group employees with their evidence counts
  const employeesWithEvidence = useMemo(() => {
    if (!checkDetails?.employee_results) return [];
    const staffList = staffData?.getEmployees?.data || [];

    const employees = [];
    checkDetails.employee_results.forEach((res) => {
      const email = res?.resource_name;
      const staffDetails = staffList.find((emp) =>
        emp?.work_email?.toLowerCase() === email?.toLowerCase()
      );

      // Get real evidence from cache
      const evidenceItems = employeeEvidenceCache[email] || [];
      const evidenceCount = evidenceItems.length;

      // Status is now directly set by checkmate, no need for exception logic
      const status = res?.status || "Unknown";

      employees.push({
        id: res?.id || email,
        employeeName: staffDetails?.name || email?.split("@")[0] || EmptyFields.UNKNOWN,
        employeeEmail: email,
        employeeRole: staffDetails?.employments?.[0]?.job_title || "-",
        joinDate: staffDetails?.start_date || "-",
        evidenceCount: evidenceCount,
        status: status,
        reason: res?.reason || "-",
        evidenceItems: evidenceItems,
      });
    });

    return employees;
  }, [checkDetails, staffData, employeeEvidenceCache]);

  // Unique role options for role filter (excluding empty / placeholder roles)
  const roleOptions = useMemo(() => {
    const roles = new Set();
    employeesWithEvidence.forEach((emp) => {
      const role = emp.employeeRole;
      if (role && role !== "-") {
        roles.add(role);
      }
    });
    return Array.from(roles).sort((a, b) => a.localeCompare(b));
  }, [employeesWithEvidence]);
  const filteredEmployees = useMemo(() => {
    let filtered = employeesWithEvidence;

    // Apply search filter
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter((emp) =>
        emp?.employeeName?.toLowerCase().includes(query) ||
        emp?.employeeEmail?.toLowerCase().includes(query) ||
        emp?.employeeRole?.toLowerCase().includes(query) ||
        emp?.reason?.toLowerCase().includes(query)
      );
    }

    // Apply join date filter
    if (joinDateFilter.mode) {
      const auditStartStr = formatDateForInput(auditStartDate);

      filtered = filtered.filter((emp) => {
        if (!emp.joinDate || emp.joinDate === "-") {
          return false;
        }

        // Handle millisecond strings (e.g., "1768780800000")
        let dateToFormat = emp.joinDate;
        if (typeof emp.joinDate === "string" && /^\d+$/.test(emp.joinDate)) {
          dateToFormat = Number(emp.joinDate);
        }

        const empJoinDateStr = formatDateForInput(dateToFormat);
        if (!empJoinDateStr) {
          return false;
        }

        if (joinDateFilter.mode === "before" && joinDateFilter.date) {
          return empJoinDateStr < joinDateFilter.date;
        }
        if (joinDateFilter.mode === "after" && joinDateFilter.date) {
          return empJoinDateStr > joinDateFilter.date;
        }

        if (joinDateFilter.mode === "between_audit_period") {
          if (!auditStartStr) return true; // no audit start; don't filter
          return empJoinDateStr >= auditStartStr;
        }

        return true;
      });
    }

    // Apply status filter
    if (statusFilter.length > 0) {
      filtered = filtered.filter((emp) =>
        statusFilter.includes((emp.status || "").toLowerCase())
      );
    }

    // Apply evidence filter
    if (evidenceFilter.length > 0) {
      filtered = filtered.filter((emp) => {
        const hasEvidence = (emp.evidenceCount || 0) > 0;
        // If both "yes" and "no" are selected, show all
        if (evidenceFilter.includes("yes") && evidenceFilter.includes("no")) {
          return true;
        }
        // If only "yes" is selected, show only those with evidence
        if (evidenceFilter.includes("yes")) {
          return hasEvidence;
        }
        // If only "no" is selected, show only those without evidence
        if (evidenceFilter.includes("no")) {
          return !hasEvidence;
        }
        return true;
      });
    }

    // Apply role filter
    if (roleFilter.length > 0) {
      filtered = filtered.filter((emp) =>
        emp.employeeRole && roleFilter.includes(emp.employeeRole)
      );
    }

    return filtered;
  }, [employeesWithEvidence, searchTerm, joinDateFilter, statusFilter, evidenceFilter, roleFilter, auditStartDate, auditEndDate]);

  // Use custom hooks for selection and download
  const {
    selectedEmployees,
    handleSelectAll,
    handleSelectEmployee,
    clearSelection,
    isAllSelected,
    isSomeSelected,
  } = useEmployeeSelection(filteredEmployees);

  const { isDownloading, downloadEvidenceFiles, downloadError } = useBulkDownload();
  const [snackbarInfo, setSnackbarInfo] = useState(null);

  const TransitionComponent = (props) => <Slide {...props} direction="left" />;

  // Show error snackbar when download fails
  useEffect(() => {
    if (downloadError) {
      setSnackbarInfo({
        message: downloadError,
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    }
  }, [downloadError]);

  const clearFilters = () => {
    setSearchTerm("");
    setJoinDateFilter({ date: "", mode: null });
    setStatusFilter([]);
    setEvidenceFilter([]);
    clearSelection();
  };

  const handleBulkDownload = () => {
    downloadEvidenceFiles(selectedEmployees, filteredEmployees);
  };

  const handleSingleEmployeeDownload = (employee) => {
    downloadEvidenceFiles([employee.id], filteredEmployees);
  };

  const employeeColumns = getEmployeeColumns({
    theme,
    isAllSelected,
    isSomeSelected,
    handleSelectAll,
    joinDateFilter,
    setJoinDateFilter,
    statusFilter,
    setStatusFilter,
    evidenceFilter,
    setEvidenceFilter,
    roleFilter,
    setRoleFilter,
    roleOptions,
  });

  const renderEmployeeCell = (row, column) => {
    if (column?.field === "checkbox") {
      const hasEvidence = (row.evidenceCount || 0) > 0;
      const isDisabled = !hasEvidence;
      
      const checkbox = (
        <Checkbox
          size="small"
          checked={selectedEmployees.includes(row.id)}
          onChange={() => handleSelectEmployee(row.id)}
          disabled={isDisabled}
          sx={{
            color: theme.palette.grey[400],
            "&.Mui-checked": {
              color: theme.palette.primary.main,
            },
            "&.Mui-disabled": {
              color: theme.palette.grey[300],
            },
          }}
        />
      );

      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", pl:1 }}>
          {isDisabled ? (
            <Tooltip
              title="No evidence available for this employee"
              arrow
              placement="top"
            >
              <span>{checkbox}</span>
            </Tooltip>
          ) : (
            checkbox
          )}
        </Box>
      );
    }
    if (column?.field === "employeeName") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Avatar sx={{ bgcolor: blue[600], mr: 2, width: 40, height: 40, fontSize: "0.9375rem", fontWeight: 600, flexShrink: 0 }}>
            {row?.employeeName?.split(" ")?.map((n) => n?.[0])?.join("") || "?"}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: "0.9375rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row?.employeeName || EmptyFields.UNKNOWN}
            </Typography>
            <Typography
                  variant="body2"
                  sx={{
                    color: theme.customColors.text.secondary,
                    fontSize: "0.875rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
              {row?.employeeEmail || "-"}
            </Typography>
          </Box>
        </Box>
      );
    }
    if (column?.field === "employeeRole") {
      const hasRole = row?.employeeRole && row?.employeeRole !== "-";
      return (
        <Box sx={{ display: "flex" }}>
          <Chip
            label={row?.employeeRole === "-" ? "Not Assigned" : row?.employeeRole}
            size="small"
            sx={{
              bgcolor: hasRole ? "rgba(26,86,219,0.08)" : "background.paper",
              color: hasRole ? "primary.main" : "text.secondary",
              fontWeight: 500,
              fontSize: "0.8125rem",
              height: "26px",
              borderRadius: "6px",
              px: 1.5,
              maxWidth: "100%",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        </Box>
      );
    }
    if (column?.field === "joinDate") {
      return (
        <Typography
          variant="body2"

          sx={{
            pl:0.5,
            fontWeight: 500,
            fontSize: "0.875rem",
            color: theme.customColors.text.secondary,
          }}
        >
          {formatDate(row.joinDate) || "-"}
        </Typography>
      );
    }
    if (column?.field === "status") {
      return (
        <ResourceStatusCell
          status={row?.status}
          exceptionValidUntil={null}
          gap={0.5}
        />
      );
    }
    if (column?.field === "reason") {
      return (
        <Typography
          variant="body2"
          sx={{
            pl:1,
            fontWeight: 500,
            fontSize: "0.875rem",
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.5,
            color: theme.customColors.text.strong,
          }}
        >
          {row.reason || "-"}
        </Typography>
      );
    }
    if (column?.field === "evidenceCount") {
      const count = row.evidenceCount || 0;

      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", pl:1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => handleSingleEmployeeDownload(row)}
            disabled={count === 0 || isDownloading}
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 600,
              minWidth: "100px",
              borderColor: count > 0 ? theme.palette.primary.main : theme.palette.grey[300],
              color: count > 0 ? theme.palette.primary.main : theme.palette.grey[500],
              "&:hover": {
                borderColor: count > 0 ? theme.palette.primary.dark : theme.palette.grey[300],
                backgroundColor: count > 0 ? theme.palette.primary.light + "10" : "transparent",
              },
              "&:disabled": {
                borderColor: theme.palette.grey[300],
                color: theme.palette.grey[400],
              },
            }}
          >
            {count} {count === 1 ? "file" : "files"}
          </Button>
        </Box>
      );
    }
    return <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.875rem" }}>{row[column?.field] || "-"}</Typography>;
  };

  const [showPolicy, setShowPolicy] = useState(false);

  // If showing policy, replace entire view (like infra activity graph)
  if (showPolicy && activeCheckId) {
    return (
      <Box sx={{ width: "100%", height: "100%" }}>
        <PolicyEvidenceTab
          checkDetails={checkDetails}
          isAuditorView={isAuditorView}
          rightAction={
            <Tooltip title="Back to Employees" placement="left">
              <IconButton
                onClick={() => setShowPolicy(false)}
                size="small"
                sx={{
                  backgroundColor: theme.palette.grey[100],
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    backgroundColor: theme.palette.grey[200],
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
          }
        />
      </Box>
    );
  }

  return (
    <>
      <EvidenceTableContainer
        title="Employees"
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        clearFilters={clearFilters}
        filteredCount={filteredEmployees.length}
        countLabel={`${filteredEmployees.length} ${
          filteredEmployees.length === 1 ? "employee" : "employees"
        } found`}
        tableExpanded={true}
        onToggleExpand={showPolicyButton ? () => setShowPolicy(true) : () => {}}
        showExpandCollapse={showPolicyButton}
        expandText="Show policy"
        selectedCount={selectedEmployees.length}
        selectedLabel="Selected"
        customAction={
          <BulkDownloadButton
            selectedCount={selectedEmployees.length}
            isDownloading={isDownloading}
            onDownload={handleBulkDownload}
          />
        }
      >
        <ReusableTable
          columns={employeeColumns}
          data={filteredEmployees}
          renderCell={renderEmployeeCell}
          showPagination={true}
          height="auto"
          rowsPerPage={50}
          rowsPerPageOptions={[10, 50, 100]}
        />
      </EvidenceTableContainer>

      <LoadingToast
        open={isDownloading}
        title="Preparing your download"
        message="Sit back and relax — this may take a few moments."
      />

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default PeopleEvidenceTab;